import { useState, createContext, useContext, useMemo, useCallback } from 'react';

import { useLocation, matchPath } from 'react-router-dom';

import { WebView } from '@zenbusiness/application-commons-webview';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { useAccountExperience } from '#client/hooks/useAccountExperience';

import { VELO_SIDE_PANEL_PAGES } from '../constants';

const VeloPanelContext = createContext();

export const useVeloPanel = () => useContext(VeloPanelContext);

const findPage = (pathname) => VELO_SIDE_PANEL_PAGES.find(({ path }) => matchPath({ path, end: true }, pathname));

export const VeloPanelProvider = ({ children }) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { accountExperience } = useAccountExperience();
  const page = findPage(pathname) || null;
  const pageKey = page?.path;

  const [isOpen, setIsOpen] = useState(true);
  const [threadUuid, setThreadUuid] = useState(null);
  const [lastPageKey, setLastPageKey] = useState(pageKey);

  if (pageKey !== lastPageKey) {
    setLastPageKey(pageKey);
    setIsOpen(true);
    setThreadUuid(null);
  }

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  const isPanelVisible =
    !!page && isOpen && isDesktop && !WebView.isWebView && accountExperience.chrome.showVeloFloatingButton;

  const value = useMemo(
    () => ({ page, isPanelVisible, openPanel, closePanel, threadUuid, setThreadUuid }),
    [page, isPanelVisible, openPanel, closePanel, threadUuid],
  );

  return <VeloPanelContext.Provider value={value}>{children}</VeloPanelContext.Provider>;
};
